import {ComponentSequenceGraphQL} from 'components/graphql/__generated/ComponentSequenceGraphQL';
import {ComponentSequenceApolloConfig} from 'components/graphql/Sequence';
import {Sequence} from 'components/Sequence';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {ComponentSequencePrimerGraphQL} from 'molbio/primers/backbone/graphql/__generated/ComponentSequencePrimerGraphQL';
import {ComponentSequencePrimerSyncConfig} from 'molbio/primers/backbone/graphql/SequencePrimer';
import SequencePrimer from 'molbio/primers/backbone/SequencePrimer';
import lazyInit from 'util/lazyInit';

export enum SyncedModelName {
  COMPONENT_SEQUENCE = 'COMPONENT_SEQUENCE',
  COMPONENT_SEQUENCE_PRIMER = 'COMPONENT_SEQUENCE_PRIMER',
}

/**
 * Maps the model name to the type of the Backbone model being synced.
 */
export interface SyncedBackboneTypes {
  [SyncedModelName.COMPONENT_SEQUENCE]: Sequence;
  [SyncedModelName.COMPONENT_SEQUENCE_PRIMER]: SequencePrimer;
}

/**
 * Maps the model name to the type of the attributes that are synced to GraphQL. These types should be
 * generated by extract-gql.
 */
export interface SyncedGraphQLTypes {
  [SyncedModelName.COMPONENT_SEQUENCE]: ComponentSequenceGraphQL;
  [SyncedModelName.COMPONENT_SEQUENCE_PRIMER]: ComponentSequencePrimerGraphQL;
}

/**
 * This interface defines what a Backbone model config will need in order to be synced successfully to the
 * Apollo store. Configs that extend from this should go in their own file in a graphql/ folder
 * in the same location as the corresponding Backbone model
 * (e.g. components/Sequence's config goes in components/graphql/Sequence).
 */
export interface SyncedModelConfig<K extends SyncedModelName> {
  /**
   * __typename should match the name of the node in MolBioTypeDefs and should end with 'Node'. It is here to
   * make autogenerating nested model identifiers easier (see getNestedIdentifier).
   */
  __typename: SyncedGraphQLTypes[K]['__typename'];

  fragmentWrapper: FragmentWrapper<SyncedGraphQLTypes[K]>;

  /**
   * Returns all of the attributes that should be synced to the Apollo store. These attributes should match
   * the fields listed in MolBioTypeDefs.
   */
  getGraphQLAttrs(model: SyncedBackboneTypes[K]): SyncedGraphQLTypes[K];

  /**
   * Returns the identifier used in the Apollo cache. This will most likely be a model's API id, but for
   * models that don't correspond directly to a server model, or don't consistently have ids, this may be
   * something else like the Backbone cid or a uuid.
   */
  getId(model: SyncedBackboneTypes[K]): string;
}

type SyncedModelConfigs = {
  [K in SyncedModelName]: SyncedModelConfig<K>;
};

const lazySyncedModelConfigs = lazyInit<SyncedModelConfigs>(() => ({
  [SyncedModelName.COMPONENT_SEQUENCE]: ComponentSequenceApolloConfig,
  [SyncedModelName.COMPONENT_SEQUENCE_PRIMER]: ComponentSequencePrimerSyncConfig,
}));

/**
 * Tiny utility function to let TS know at callsites which model the returned config is for
 */
export function getModelConfig<K extends SyncedModelName>(modelName: K): SyncedModelConfig<K> {
  // There's no way to specify that K *is* of type SyncedModelName, not an extension of SyncedModelName
  return lazySyncedModelConfigs()[modelName] as unknown as SyncedModelConfig<K>;
}
